import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Badge, Spinner, Row, Col, Card, CardBody, CardTitle, CardText,
  Input, Label, Button,
} from 'reactstrap';
import { PageHeader } from '../components/PageHeader';
import { OverviewGrid } from '../components/OverviewGrid';
import { DataTable } from '../components/DataTable';
import { pulseFetch } from '../pulse/pulseClient';
import { useAuth } from '../auth/AuthProvider';

const ZABBIX_URL = 'https://zabbix.dev.valitype.com';

function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function usePulseHealth() {
  return useQuery({
    queryKey: ['pulse-health'],
    queryFn: () => pulseFetch('/v1/healthz'),
    refetchInterval: 30_000,
    retry: false,
  });
}

// ── Übersicht ──────────────────────────────────────────────────────────
const CARDS = [
  { to: '/admin/settings/pulse/security', icon: '🛡️', title: 'Security-Dashboard', status: 'live',
    description: 'Alerts + Security-Events aller Quellen (CrowdSec, Nexus, Orbit, MTFF) mit Ack.' },
  { to: '/admin/settings/pulse/crowdsec', icon: '🚨', title: 'CrowdSec-Statistiken', status: 'live',
    description: 'Aktive CrowdSec-Decisions aggregiert nach Szenario / Type / Origin.' },
  { href: ZABBIX_URL, icon: '📈', title: 'Zabbix', status: 'external',
    description: 'Infrastruktur-Monitoring. Pusht Kennzahlen als source=zabbix in Pulse.' },
  { to: '/admin/settings/pulse/telemetry', icon: '🛰️', title: 'Telemetry', status: 'live',
    description: 'OpenTelemetry-/Prometheus-Endpoints der Services + Pulse-Ingestion.' },
  { to: '/admin/settings/pulse/cpguard', icon: '📊', title: 'cpGuard', status: 'live',
    description: 'Custom-Metriken (source=cpguard) als Timeseries aus /v1/metrics.' },
];

export function PulseOverviewPage() {
  const health = usePulseHealth();
  return (
    <div>
      <PageHeader
        title="Pulse API · Übersicht"
        subtitle="Observability- & Security-Aggregation. Zugriff über /proxy/pulse (CORE-Bearer)."
      />
      <Alert color={health.isError ? 'danger' : 'light'} className="d-flex align-items-center gap-2 small">
        <strong>API-Status:</strong>
        {health.isLoading ? (
          <><Spinner size="sm" /> prüfe …</>
        ) : health.isError ? (
          <Badge color="danger" pill>nicht erreichbar</Badge>
        ) : (
          <>
            <Badge color="success" pill>ok</Badge>
            <span className="text-muted">pulse · {health.data?.env ?? 'unbekannt'}</span>
          </>
        )}
      </Alert>
      <OverviewGrid cards={CARDS} />
    </div>
  );
}

// ── Zabbix ─────────────────────────────────────────────────────────────
export function PulseZabbixPage() {
  return (
    <div>
      <PageHeader title="Zabbix · Monitoring" subtitle="Infrastruktur-Monitoring — externe Konsole + Pulse-Ingestion." />
      <Row className="g-3">
        <Col md="7">
          <Card className="h-100">
            <CardBody>
              <CardTitle tag="h6">Zabbix-Konsole</CardTitle>
              <CardText className="text-muted small">
                Zabbix läuft dediziert unter <code>zabbix.dev.valitype.com</code> und überwacht
                Nodes, CNPG, Kong, Traefik und die API-Deployments. Die Web-UI wird nicht in
                Orbit eingebettet (eigene Auth) — direkt öffnen:
              </CardText>
              <Button color="primary" tag="a" href={ZABBIX_URL} target="_blank" rel="noreferrer">
                Zabbix öffnen ↗
              </Button>
            </CardBody>
          </Card>
        </Col>
        <Col md="5">
          <Card className="h-100">
            <CardBody>
              <CardTitle tag="h6">Pulse-Anbindung</CardTitle>
              <CardText className="text-muted small mb-1">
                Ein Zabbix-Poller schreibt ausgewählte Kennzahlen als
                <code className="mx-1">source=zabbix</code> über
                <code className="mx-1">POST /v1/metrics/events</code> in Pulse.
              </CardText>
              <CardText className="text-muted small mb-0">
                Auswertung als Timeseries unter <strong>cpGuard</strong> bzw. im
                <strong> Security-Dashboard</strong>.
              </CardText>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// ── Telemetry ──────────────────────────────────────────────────────────
const TELEMETRY_ENDPOINTS = [
  { svc: 'core',   scrape: 'core.api.valitype.net/metrics',   note: 'HTTP-Latenz, Requests, DB-Pool.' },
  { svc: 'nexus',  scrape: 'nexus.api.valitype.net/metrics',  note: 'Job-Queue, Plugin-Calls, Saga.' },
  { svc: 'pulse',  scrape: 'pulse.api.valitype.net/metrics',  note: 'Ingestion-Rate, Alert-Backlog.' },
  { svc: 'orbit',  scrape: 'orbit.api.valitype.net/metrics',  note: 'Ticket-Worker, Mail-Outbox.' },
];

export function PulseTelemetryPage() {
  return (
    <div>
      <PageHeader title="Telemetry · Endpoints" subtitle="OpenTelemetry / Prometheus-Scrape-Targets + Pulse-Ingestion." />
      <Alert color="light" className="small">
        Jeder Service exponiert ein Prometheus-<code>/metrics</code>-Target. Pulse aggregiert
        zusätzlich Custom-Samples in <code>metric_samples</code> (via <code>/v1/metrics</code>),
        die unter <strong>cpGuard</strong> als Timeseries lesbar sind.
      </Alert>
      <Card>
        <CardBody>
          <DataTable empty={false}>
            <thead>
              <tr><th>Service</th><th>Scrape-Target</th><th>Kennzahlen</th></tr>
            </thead>
            <tbody>
              {TELEMETRY_ENDPOINTS.map((e) => (
                <tr key={e.svc}>
                  <td><Badge color="secondary" pill>{e.svc}</Badge></td>
                  <td className="font-monospace small">{e.scrape}</td>
                  <td className="text-muted small">{e.note}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </CardBody>
      </Card>
    </div>
  );
}

// ── cpGuard (Timeseries-Viewer gegen /v1/metrics/timeseries) ────────────
function useTimeseries({ metric, from, to, enabled }) {
  const auth = useAuth();
  return useQuery({
    queryKey: ['pulse-timeseries', { metric, from, to }],
    enabled: enabled && !!metric,
    retry: false,
    queryFn: () => {
      const qs = new URLSearchParams({ metric, from, to });
      return pulseFetch(`/v1/metrics/timeseries?${qs.toString()}`, {
        headers: authHeader(auth.getAccessToken()),
      });
    },
  });
}

export function PulseCpGuardPage() {
  const [metricInput, setMetricInput] = useState('cpguard.cpu.load');
  const [metric, setMetric] = useState('cpguard.cpu.load');
  const [days, setDays] = useState(1);

  const { from, to } = useMemo(() => {
    const now = new Date();
    const past = new Date(now.getTime() - days * 86_400_000);
    return { from: past.toISOString(), to: now.toISOString() };
  }, [days]);

  const q = useTimeseries({ metric, from, to, enabled: true });
  const points = q.data?.points ?? [];
  const max = Math.max(1, ...points.map((p) => p.v));
  const last = points.length ? points[points.length - 1].v : null;
  const avg = points.length ? points.reduce((a, p) => a + p.v, 0) / points.length : null;

  return (
    <div>
      <PageHeader
        title="cpGuard · Custom-Metriken"
        subtitle="Timeseries aus Pulse metric_samples (source=cpguard / zabbix) · GET /v1/metrics/timeseries"
      />

      <Card className="mb-3">
        <CardBody>
          <Row className="g-2 align-items-end">
            <Col md="5">
              <Label className="small mb-1">Metric-Name</Label>
              <Input
                bsSize="sm"
                value={metricInput}
                onChange={(e) => setMetricInput(e.target.value)}
                placeholder="z. B. cpguard.cpu.load"
                onKeyDown={(e) => e.key === 'Enter' && setMetric(metricInput.trim())}
              />
            </Col>
            <Col md="3">
              <Label className="small mb-1">Zeitraum</Label>
              <Input type="select" bsSize="sm" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                <option value={1}>24 Stunden</option>
                <option value={7}>7 Tage</option>
                <option value={30}>30 Tage</option>
              </Input>
            </Col>
            <Col md="2">
              <Button color="primary" size="sm" onClick={() => setMetric(metricInput.trim())}>
                Laden
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {q.isError && (
        <Alert color="danger" className="small">{q.error?.message ?? 'Abfrage fehlgeschlagen.'}</Alert>
      )}

      <Row className="g-3 mb-3">
        <Col md="4"><Card><CardBody className="text-center">
          <div className="fw-bold" style={{ fontSize: '1.6rem' }}>{last != null ? last.toFixed(2) : '—'}</div>
          <div className="text-muted small">Letzter Wert {q.data?.unit ? `(${q.data.unit})` : ''}</div>
        </CardBody></Card></Col>
        <Col md="4"><Card><CardBody className="text-center">
          <div className="fw-bold" style={{ fontSize: '1.6rem' }}>{avg != null ? avg.toFixed(2) : '—'}</div>
          <div className="text-muted small">Durchschnitt</div>
        </CardBody></Card></Col>
        <Col md="4"><Card><CardBody className="text-center">
          <div className="fw-bold" style={{ fontSize: '1.6rem' }}>{points.length}</div>
          <div className="text-muted small">Datenpunkte</div>
        </CardBody></Card></Col>
      </Row>

      <Card>
        <CardBody>
          <CardTitle tag="h6" className="mb-3">
            <code>{metric}</code>
            {q.isLoading && <Spinner size="sm" className="ms-2" />}
          </CardTitle>
          {/* Schlichter Inline-Sparkline-Balken pro Bucket — kein Chart-Dep nötig. */}
          {points.length === 0 && !q.isLoading ? (
            <p className="text-muted small mb-0">
              Keine Datenpunkte für diese Metrik im Zeitraum. Prüfe den Metric-Namen —
              cpGuard/Zabbix schreiben über <code>POST /v1/metrics/events</code>.
            </p>
          ) : (
            <div className="d-flex align-items-end gap-1" style={{ height: 120 }}>
              {points.map((p) => (
                <div
                  key={p.t}
                  title={`${new Date(p.t).toLocaleString('de-DE')} · ${p.v}`}
                  style={{
                    flex: 1,
                    minWidth: 2,
                    height: `${Math.max(2, (p.v / max) * 100)}%`,
                    background: 'var(--bs-primary, #5a4fcf)',
                    borderRadius: 2,
                  }}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
